// Cloudflare API Service
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ccro-api.ccroinida.workers.dev';

interface ApiResponse<T = any> {
  success?: boolean;
  error?: string;
  data?: T;
  [key: string]: any;
}

class ApiService {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.token = localStorage.getItem('authToken');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('authToken');
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const rawBody = await response.text();
      let data: any = {};
      try {
        data = rawBody ? JSON.parse(rawBody) : {};
      } catch {
        data = { error: rawBody || `HTTP error! status: ${response.status}` };
      }

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error: any) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const response = await this.request<{
      success: boolean;
      token?: string;
      otpRequired?: boolean;
      message?: string;
      user: {
        uid: string;
        email: string;
        displayName: string;
        role: string;
      };
    }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.success && response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  async resetPassword(email: string, otp: string, newPassword: string) {
    return this.request<{ success: boolean; message?: string }>(`/api/auth/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ email, otp, newPassword }),
    });
  }

  async sendOtp(email: string, purpose: string = 'login') {
    return this.request<{ success: boolean; message?: string }>(`/api/auth/send-otp`, {
      method: 'POST',
      body: JSON.stringify({ email, purpose }),
    });
  }

  async resendOtp(email: string, purpose: string = 'login') {
    return this.request<{ success: boolean; message?: string }>(`/api/auth/resend-otp`, {
      method: 'POST',
      body: JSON.stringify({ email, purpose }),
    });
  }

  async verifyOtp(email: string, otp: string, purpose: string = 'login') {
    const response = await this.request<{
      success: boolean;
      token: string;
      user: {
        uid: string;
        email: string;
        displayName: string;
        role: string;
      };
    }>(`/api/auth/verify-otp`, {
      method: 'POST',
      body: JSON.stringify({ email, otp, purpose }),
    });

    if (response.success && response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  async googleLogin(credential: string) {
    const response = await this.request<{
      success: boolean;
      token?: string;
      isNewUser?: boolean;
      user?: {
        uid: string;
        email: string;
        displayName: string | null;
        role: string;
      };
    }>('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });

    if (response.success && response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  async register(email: string, password: string, fullName: string, phone?: string) {
    const response = await this.request<{
      success: boolean;
      token: string;
      user: {
        uid: string;
        email: string;
      };
    }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, fullName, phone }),
    });

    if (response.success && response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  async getCurrentUser() {
    return this.request<{
      user: {
        uid: string;
        email: string;
        displayName: string;
        role: string;
      };
      details: any;
    }>('/api/auth/me');
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('userLoginTime');
  }

  // Users endpoints
  async getUser(userId: string) {
    return this.request<any>(`/api/users/${userId}`);
  }

  async updateUser(userId: string, data: any) {
    return this.request(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getMembershipPlans() {
    return this.request<{ plans: any[] }>('/api/membership/plans');
  }

  async getMembershipStatus() {
    return this.request<{
      paid: boolean;
      status: string;
      planId?: string | null;
      paidAt?: string | null;
      memberType?: string;
      memberStatus?: string;
      validFrom?: string | null;
      validUntil?: string | null;
      grantedByAdmin: boolean;
      grantedAt?: string | null;
      plan?: {
        id: string;
        name: string;
        memberType: string;
        baseAmount: number;
        maintenanceAmount: number;
        gatewayFee: number;
        feePercent: number;
        totalAmount: number;
      } | null;
      payments: Array<{
        orderId: string;
        paymentId: string | null;
        status: string;
        currency: string;
        baseAmount: number;
        maintenanceAmount: number;
        gatewayFee: number;
        totalAmount: number;
        createdAt: string;
        paidAt: string | null;
      }>;
    }>('/api/membership/status');
  }

  async createMembershipOrder(planId: string) {
    return this.request<{ success: boolean; keyId: string; order: any; plan: any }>('/api/membership/create-order', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  }

  async verifyMembershipPayment(payment: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) {
    return this.request<{ success: boolean; memberType?: string }>('/api/membership/verify-payment', {
      method: 'POST',
      body: JSON.stringify(payment),
    });
  }

  // Storage endpoints
  async uploadFile(userId: string, type: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/api/storage/upload/${userId}/${type}`;
    const headers: Record<string, string> = {};

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    return response.json();
  }

  getFileUrl(userId: string, type: string) {
    return `${this.baseUrl}/api/storage/file/${userId}/${type}`;
  }

  async deleteFile(userId: string, type: string) {
    return this.request(`/api/storage/file/${userId}/${type}`, {
      method: 'DELETE',
    });
  }

  // Public members (no auth required)
  async getPublicMembers(memberType?: string, memberStatus?: string) {
    const params = new URLSearchParams();
    if (memberType) params.append('memberType', memberType);
    if (memberStatus) params.append('memberStatus', memberStatus);
    const queryString = params.toString();
    return this.request<{ members: any[] }>(`/api/users/public${queryString ? `?${queryString}` : ''}`);
  }

  async getPublicMember(enrollmentNumber: string) {
    return this.request<{ member: any }>(`/api/users/public/member/${encodeURIComponent(enrollmentNumber)}`);
  }

  // Public member counts per member type (no auth required) — drives nav visibility
  async getPublicMemberCounts() {
    return this.request<{ counts: Array<{ member_type: string; count: number }> }>('/api/users/public/counts');
  }

  // Articles endpoints
  async getArticles(options?: { limit?: number; offset?: number; category?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.category) params.append('category', options.category);
    const queryString = params.toString();
    return this.request<{ articles: any[]; total: number }>(`/api/articles${queryString ? `?${queryString}` : ''}`);
  }

  async getArticle(id: string) {
    return this.request<{ article: any }>(`/api/articles/${id}`);
  }

  async getMyArticles() {
    return this.request<{ articles: any[] }>('/api/articles/my/articles');
  }

  async createArticle(data: any) {
    return this.request('/api/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateArticle(id: string, data: any) {
    return this.request(`/api/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteArticle(id: string) {
    return this.request(`/api/articles/${id}`, {
      method: 'DELETE',
    });
  }

  // Events endpoints
  async getEvents(options?: { limit?: number; offset?: number; upcoming?: boolean }) {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.upcoming) params.append('upcoming', 'true');
    const queryString = params.toString();
    return this.request<{ events: any[]; total: number }>(`/api/events${queryString ? `?${queryString}` : ''}`);
  }

  async getEvent(id: string) {
    return this.request<{ event: any }>(`/api/events/${id}`);
  }

  // Photo galleries endpoints
  async getPhotoGalleries(category?: string) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    const queryString = params.toString();
    return this.request<{ galleries: any[]; categories: string[] }>(`/api/galleries/photos${queryString ? `?${queryString}` : ''}`);
  }

  async getPhotoGallery(id: string) {
    return this.request<{ gallery: any }>(`/api/galleries/photos/${id}`);
  }

  // Video galleries endpoints
  async getVideoGalleries() {
    return this.request<{ galleries: any[] }>('/api/galleries/videos');
  }

  async getVideoGallery(id: string) {
    return this.request<{ gallery: any }>(`/api/galleries/videos/${id}`);
  }

  // Contact form (Cloudflare Worker)
  async submitContactMessage(data: { name: string; email: string; message: string }) {
    return this.request<{ success: boolean; id?: string }>('/api/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Upload an article image (Cloudflare Worker → R2, public URL)
  async uploadArticleImage(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseUrl}/api/storage/articles`;
    const headers: Record<string, string> = {};

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    return response.json();
  }

  // Health check
  async healthCheck() {
    return this.request<{ status: string; message: string }>('/');
  }
}

export const api = new ApiService(API_BASE_URL);
export default api;
